<template>
  <div>
    <div v-if="regionData" id="agency-page">
      <main-section v-if="metaInfo" :meta-info="metaInfo" section-id="" title="" :sections="sections" image-url="" />
      <div class="agency">
        <structured-json-seo :region-data="regionData" />
        <header class="agency__hero">
          <div class="agency-title">
            <h1 ref="agencyCityHeading" class="agency-city">{{ pageTitle }}</h1>
          </div>
          <div class="office-actions">
            <form-button text="Tilaa arviokäynti" theme="primary" tabindex="0" @click="openAgencyForm()" />
          </div>
        </header>

        <call-to-action :actions="callToActionHeaderContent" />
        <div class="main-section">
          <div class="section-wrapper opds-color-background-default">
            <three-cards :content="threeCardsContent" custom-class="list-blocks" />
          </div>
        </div>

        <div v-if="news" id="agency-news-container">
          <div v-if="news.length > 0">
            <div v-for="newsObj in news" id="agency-news" :key="newsObj.name">
              <h2 class="company-title">{{ newsObj.name }}</h2>
              <template v-if="newsObj.news.length > 0">
                <div v-for="newsItem in newsObj.news" :key="`news-item-${newsItem.id}`">
                  <div class="agency-news-container">
                    <p class="title">{{ newsItem.title }}</p>
                    <p class="content">{{ newsItem.body }}</p>
                    <div class="action-button-container">
                      <template v-for="(link, index) in newsItem.links" :key="`news-item-${newsItem.id}-link-${index}`">
                        <a
                          :href="link.url"
                          class="button"
                          :target="
                            link.url.startsWith('#') || link.url.startsWith('https://op-koti.fi') ? '_self' : '_blank'
                          "
                        >
                          {{ link.title }}
                        </a>
                      </template>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <section v-if="uniqueAgents?.length" class="agent__details" aria-labelledby="agent-section-header">
          <h2 id="agent-section-header">Sinua palvelevat kiinteistönvälittäjät</h2>
          <div class="agent__container">
            <p class="agent__ingress">
              Kun ostat tai myyt asuntoa, saat meiltä asiantuntijan avuksesi. Olemme mukanasi koko asunnonvaihdon ajan.
              OP Kodin vahvan osaamisen, paikallistuntemuksen ja uusimpien digitaalisten palveluiden avulla onnistut.
            </p>
            <persons v-if="uniqueAgents?.length" :persons="uniqueAgents" />
          </div>
        </section>

        <companies v-if="uniqueCompanies?.length" :companies="uniqueCompanies" :title="getCompanyTitle" />

        <offices
          v-if="uniqueOfficeCities?.length"
          :offices="uniqueOfficeCities"
          title="Toimistojen yhteystiedot"
          :are-business-area-offices="true"
          :scroll-to-id="officesId"
        />

        <basic-article v-if="basicArticleContent.content.text" :article="basicArticleContent.content"></basic-article>

        <div v-if="listings?.length" class="line-divider"></div>
        <section v-if="listings?.length" class="agency__listings">
          <div class="agency__listings__wrapper">
            <suggested-listings :title="`Uusimmat myytävät asunnot - ${companyName}`" :suggestions="listings">
              <template #footnote>
                <div>
                  <action-link :to="{ path: apartments, query: { companyId: companyId } }"
                    >Näytä kaikki välittämämme asunnot</action-link
                  >
                </div>
              </template>
            </suggested-listings>
          </div>
        </section>

        <call-to-action :actions="callToActionContent" />

        <top-agencies />
      </div>
    </div>
    <loading v-else-if="isPending" />
    <error-message v-else-if="error" />
    <dialogue identifier="agency-dialogue">
      <template #dialogueContent>
        <agency-contact :region-name="region?.name" :office="office" :person="person" />
      </template>
    </dialogue>
  </div>
</template>
<script lang="ts">
import * as actions from '@/app/store/action-types';
import MainSection from '@/app/components/main-section/main-section.vue';
import { search } from '@/app/routes';
import config from '@/config';
import Persons from '@/app/components/persons/persons.vue';
import Offices from '@/app/components/offices/offices.vue';
import Companies from '@/app/components/companies/companies.vue';
import BasicArticle from '@/app/components/main-section/basic-article.vue';
import ActionLink from '@/app/components/action-link/action-link.vue';
import StructuredJsonSeo from '@/app/components/structured-json-seo/structured-json-seo.vue';
import VueScrollTo from 'vue-scrollto';
import { computed, defineAsyncComponent, defineComponent, ref, watch } from 'vue';
import CallToAction from '@/app/components/main-section/call-to-action.vue';
import ErrorMessage from '@/app/components/error-message/error-message.vue';
import Loading from '@/app/components/loading/loading.vue';
import useRegionData from '@/app/data/useRegionData';
import { useListingsData } from '@/app/data/useListingsData';
import { getAgencyNewsQueryKey } from '@/app/data/useAgencyNewsData';
import { agencyNewsQuery_Client } from '@/app/data/agencyNews.client';
import { useQueryClient } from '@tanstack/vue-query';
import useSafeRoute, { getRouteParamAsString } from '@/app/composables/useSafeRoute';
import isSSR from '@/utils/isSSR';
import type { BasicArticleSectionType, CtaLinkType } from '@/schemas/landingPageSchema';
import { useFocusWithWatch } from '@/app/composables/useFocusWithWatch';
import utils from '@/app/utils/utils';

export default defineComponent({
  name: 'Agency',
  components: {
    MainSection,
    BasicArticle,
    ActionLink,
    StructuredJsonSeo,
    CallToAction,
    Dialogue: defineAsyncComponent(() => import('@/app/components/dialogue/dialogue.vue')),
    FormButton: defineAsyncComponent(() => import('@/app/components/form-button/form-button.vue')),
    AgencyContact: defineAsyncComponent(() => import('@/app/components/agency-contact/agency-contact.vue')),
    Persons,
    Offices,
    Companies,
    TopAgencies: defineAsyncComponent(() => import('@/app/components/top-agencies/top-agencies.vue')),
    SuggestedListings: defineAsyncComponent(() => import('@/app/components/suggested-listings/suggested-listings.vue')),
    ErrorMessage,
    Loading,
    ThreeCards: defineAsyncComponent(() => import('@/app/components/three-cards/three-cards.vue')),
  },
  setup() {
    const route = useSafeRoute();
    const regionId = computed(() => {
      const id = getRouteParamAsString(route.value.params.id);
      return id || '';
    });
    const { data: regionData, error, isPending } = useRegionData(regionId);
    const news = ref([] as any[]);

    const uniqueAgents = computed(() => regionData.value?.agents);

    const uniqueOfficeCities = computed(() => regionData.value?.offices);

    const uniqueCompanies = computed(() => regionData.value?.companies);

    const region = computed(() => regionData.value?.companyRegion);

    const companyName = computed(() => {
      if (region.value?.businessInfo?.companyName) {
        return region.value.businessInfo.companyName;
      } else if (uniqueCompanies.value && uniqueCompanies.value.length > 0) {
        return uniqueCompanies.value[0].name;
      } else {
        return '';
      }
    });

    const companyId = computed(() => {
      if (region.value?.businessInfo?.companyId) {
        return region.value.businessInfo.companyId;
      } else if (uniqueCompanies.value && uniqueCompanies.value.length > 0) {
        return uniqueCompanies.value[0].id;
      } else {
        return undefined;
      }
    });

    // Get agency listings
    const filters = computed(() => ({
      companyId: companyId.value,
      featureGroup: 'apartment',
      limit: 4,
    }));
    const listingsEnabled = computed(() => companyId.value !== undefined);
    const { data: listingsData } = useListingsData(filters, listingsEnabled);
    const listings = computed(() => listingsData?.value?.listings);

    // Get news for each agency on the client side
    const client = useQueryClient();
    if (!isSSR) {
      watch(
        uniqueCompanies,
        async (companies) => {
          if (companies) {
            for (const company of companies) {
              // using for of loop to ensure that the news are fetched in sequence to avoid overloading server
              const data = await client.fetchQuery({
                queryKey: getAgencyNewsQueryKey(company.id),
                queryFn: () => agencyNewsQuery_Client(company.id),
              });
              if (data?.length > 0) {
                const companyExists = news.value.some(function (x: any) {
                  return x.name === company.name;
                });
                if (!companyExists) {
                  news.value.push({ name: company.name, news: data });
                }
              }
            }
          }
        },
        { immediate: true, deep: true }
      );
    }

    const agencyCityHeading = ref<HTMLElement | null>(null);

    useFocusWithWatch(regionData, agencyCityHeading);

    return {
      regionData,
      error,
      isPending,
      listings,
      uniqueAgents,
      uniqueOfficeCities,
      uniqueCompanies,
      region,
      companyName,
      companyId,
      news,
      route,
      agencyCityHeading,
    };
  },
  data: () => ({
    person: null as any,
    office: null as any,
    loading: true,
    officesId: 'offices-section-header',
    agentsId: 'agent-section-header',
    threeCardsContent: {
      cardType: 'boxed',
      title: 'Ylivertainen kokonaisuus asuntosi myyntiin',
      sections: [
        {
          text: 'Laadukkaat kuvat ja videot sekä aktiivinen myynti ja markkinointi.',
          link: { text: 'Asunnon hinnoittelu', route: '/asunnon-markkinointi' },
        },
        {
          text: 'Seuraa mm. myynti-ilmoituksesi kävijämääriä ja kommentteja esittelyistä.',
          link: { text: 'Asunnon valmistelu myyntikuntoon', route: '/myyjan-palvelu' },
        },
        {
          text: 'Testaa hinta-arviolaskurilla asuntosi arvo - tarkemman arvion saat välittäjältä.',
          link: { text: 'Tehokas markkinointi', route: '/asunnon-hintalaskuri' },
        },
        {
          text: 'Asunnonvaihdon voit halutessasi hoitaa täysin digitaalisesti.',
          link: { text: 'Luotettava asuntokauppa', route: '/digitaalinen-asuntokauppa' },
        },
      ],
    },
    callToActionHeaderContent: [
      {
        type: 'agent-contact-information',
        name: 'Välittäjien yhteystiedot',
        iconClass: 'icon-person-circled',
      },
      {
        type: 'agency-contact-information',
        name: 'Toimistojen yhteystiedot',
        iconClass: 'icon-home-circled',
      },
      {
        type: 'link',
        name: 'Testaa asuntosi arvo',
        iconClass: 'icon-loans-circled',
        route: '/asunnon-hintalaskuri',
      },
    ] satisfies CtaLinkType[],
    callToActionContent: [
      {
        type: 'link',
        name: 'Myytävät asunnot',
        route: '/myytavat/asunnot',
        iconClass: 'icon-search-circled',
      },
      {
        type: 'contact-agency',
        name: 'Ota yhteyttä',
        iconClass: 'icon-speech-bubble-circled',
      },
    ] satisfies CtaLinkType[],
    formAnalytics: {
      eVar21: 'kiinteistovalitys',
      eVar22: 'viesti-valittajalle',
    },
  }),
  computed: {
    metaInfo() {
      return {
        title: `Kiinteistönvälitys ${this.cityName} - OP Koti`,
        meta: [{ vmid: 'description', name: 'description', content: this.metaDescription }],
      };
    },
    getCompanyTitle() {
      return this.uniqueCompanies?.length === 1 ? 'Yrityksen yhteystiedot' : 'Yritysten yhteystiedot';
    },
    pageTitle() {
      return `Kiinteistönvälitys ${this.cityName}`;
    },
    cityName() {
      return this.region?.name ? this.region.name : '';
    },
    metaDescription() {
      return `Luotettavaa ja asiantuntevaa kiinteistönvälitystä paikkakunnalla ${this.cityName}. Ota yhteyttä – tehdään arvio sekä myyntisuunnitelma asunnostasi.`;
    },
    sections() {
      return [];
    },
    apartments() {
      return search.sale.routes.find((route) => route.id === config.testing.NAVIGATION.BUYING.APARTMENTS)?.path || '';
    },
    basicArticleContent() {
      return {
        sectionId: 'basic-article',
        content: {
          imagePosition: 'right',
          heading: `Kiinteistövälitys ${this.cityName}`,
          text:
            this.region?.businessInfo?.description && this.region.businessInfo.description.length > 0
              ? this.region.businessInfo.description.join()
              : '',
          imageUrl: 'https://op-koti.fi/assets/op-koti-sign.png',
          imageType: 'contain',
        },
        bgColor: 'white',
      } satisfies BasicArticleSectionType;
    },
  },
  mounted() {
    this.sendPageAnalytics();

    this.$bus.$on(
      'openPersonContactDialogue',
      (event) => {
        this.person = event.person;
        this.office = event.office;
        this.$bus.$emit('openDialogue', 'agency-dialogue');
      },
      this
    );

    this.$bus.$on(
      'dialogueClosed',
      () => {
        this.person = null;
        this.office = null;
      },
      this
    );

    this.$bus.$on(
      'scrollToAgentInformation',
      () => {
        this.scrollToHeader(this.agentsId);
      },
      this
    );

    this.$bus.$on(
      'scrollToAgencyInformation',
      () => {
        this.scrollToHeader(this.officesId);
      },
      this
    );

    this.openContactFormIfNecessary();
  },
  beforeUnmount() {
    this.$bus.$off('openPersonContactDialogue', this);
    this.$bus.$off('scrollToAgentInformation', this);
    this.$bus.$off('scrollToAgencyInformation', this);
    this.$bus.$off('dialogueClosed', this);
  },

  methods: {
    scrollToHeader(elementId: string) {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        utils.setFocusForAccessibility(element, { preventScroll: true });
      }
    },
    openAgencyForm() {
      this.person = null;
      this.$bus.$emit('openDialogue', 'agency-dialogue');
    },
    sendPageAnalytics() {
      const agencyName = this.$filters.capitalizeFirstLetter(this.$route.params.id);
      void this.$store.dispatch(actions.analytics.set, {
        pageName: `kiinteistonvalitys:${this.$route.params.id}`,
        agencyName: agencyName,
        eVar17: this.metaInfo.title,
      });
      void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.pageView);
    },
    scrollToTop() {
      const duration = 400;
      const options = {
        easing: 'ease-out',
        offset: -50,
        cancelable: true,
      };
      VueScrollTo.scrollTo(document.getElementById('vue-app'), duration, options);
    },
    openContactFormIfNecessary() {
      const contact = this.route.query.contact?.toString() || '';
      if (contact.toLowerCase() === 'true') {
        this.openAgencyForm();
      }
    },
  },
});
</script>
